import { useState, useEffect, useCallback, useRef } from 'react';
import PageIntro from './PageIntro';

// Same text as HOME_INTRO in lib/prerender.js (the server paints it before React loads)
export const HOME_INTRO = {
  title: 'Latest Amazon Deals',
  text: 'Discover the best Amazon deals, discounts, and promo codes. Save money on electronics, home goods, fashion, and more with our curated daily deals.',
};

const styles = {
  container: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: '8px',
    marginBottom: '20px',
    backgroundColor: '#f5f5f5',
  },
  slideWrapper: {
    position: 'relative',
    width: '100%',
  },
  slide: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    opacity: 0,
    transition: 'opacity 0.6s ease-in-out',
    pointerEvents: 'none',
  },
  slideActive: {
    opacity: 1,
    position: 'relative',
    pointerEvents: 'auto',
  },
  image: {
    width: '100%',
    height: '180px',
    objectFit: 'cover',
    objectPosition: 'top',
    display: 'block',
    cursor: 'pointer',
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: 'rgba(0,0,0,0.4)',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    fontSize: '14px',
    cursor: 'pointer',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
  },
  arrowLeft: {
    left: '12px',
  },
  arrowRight: {
    right: '12px',
  },
  dots: {
    position: 'absolute',
    bottom: '6px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '8px',
    zIndex: 2,
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.5)',
    border: '1px solid rgba(255,255,255,0.8)',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
    padding: 0,
  },
  dotActive: {
    backgroundColor: '#fff',
  },
};

const HeroBannerCarousel = () => {
  const [banners, setBanners] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await fetch('/api/promotions/banners');
        if (res.ok) {
          const data = await res.json();
          setBanners(data);
        }
      } catch (err) {
        console.error('Failed to fetch banners:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }, []);

  const goTo = useCallback((index) => {
    setCurrent(index);
  }, []);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % banners.length);
  }, [banners.length]);

  const prev = useCallback(() => {
    setCurrent((prev) => (prev - 1 + banners.length) % banners.length);
  }, [banners.length]);

  // Auto-rotate
  useEffect(() => {
    if (banners.length <= 1) return;
    timerRef.current = setInterval(next, 5000);
    return () => clearInterval(timerRef.current);
  }, [banners.length, next]);

  // Pause on hover
  const pauseTimer = () => clearInterval(timerRef.current);
  const resumeTimer = () => {
    if (banners.length <= 1) return;
    timerRef.current = setInterval(next, 5000);
  };

  // Render the static header while banners load: returning null here made the
  // whole page below it jump ~375px once the request finished (CLS ≈ 0.5).
  if (loading || banners.length === 0) {
    return <PageIntro title={HOME_INTRO.title} text={HOME_INTRO.text} />;
  }

  return (
    <div
      style={styles.container}
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeTimer}
    >
      <div style={styles.slideWrapper}>
        {banners.map((banner, i) => (
          <div
            key={banner._id}
            style={{
              ...styles.slide,
              ...(i === current ? styles.slideActive : {}),
            }}
          >
            <a
              href={banner.affiliate_link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={banner.image_url}
                alt={banner.alt_text || banner.title || 'Amazon Promotion'}
                style={styles.image}
                loading={i === 0 ? 'eager' : 'lazy'}
              />
            </a>
          </div>
        ))}
      </div>

      {banners.length > 1 && (
        <>
          <button
            onClick={prev}
            style={{ ...styles.arrow, ...styles.arrowLeft }}
            aria-label="Previous banner"
          >
            ‹
          </button>
          <button
            onClick={next}
            style={{ ...styles.arrow, ...styles.arrowRight }}
            aria-label="Next banner"
          >
            ›
          </button>
          <div style={styles.dots}>
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                style={{
                  ...styles.dot,
                  ...(i === current ? styles.dotActive : {}),
                }}
                aria-label={`Go to banner ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default HeroBannerCarousel;
